import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Upload, Trash2, FileSpreadsheet, Calendar, CheckCircle2, Info, Sparkles, AlertTriangle, History } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getFirms, uploadStockReport, listStockReports, deleteStockReport } from '../../services/taskApi';

const FlowStockUpload = () => {
  const { isDark, user } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [firms, setFirms] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);

  const [uploadFirm, setUploadFirm] = useState('');
  const [uploadDate, setUploadDate] = useState(new Date().toISOString().slice(0, 10));
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([getFirms(), listStockReports()]);
      setFirms(f.data || []);
      setReports(r.data || []);
      if (!uploadFirm && (f.data || []).length) setUploadFirm(f.data[0].id);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, [uploadFirm]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    if (!uploadFile) { toast.error('Select an Excel file'); return; }
    if (!uploadFirm) { toast.error('Select a firm'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('firm_id', uploadFirm);
      fd.append('report_date', uploadDate);
      const res = await uploadStockReport(fd);
      toast.success(res.data.message);
      setUploadFile(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (rep) => {
    if (!window.confirm(`Delete ${rep.firm_code} report from ${rep.report_date}?\n\nFile: ${rep.filename}`)) return;
    try {
      await deleteStockReport(rep.id);
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Helper — can current user delete a given report?
  const canDelete = (rep) => {
    if (isAdmin) return true;
    try {
      const age = (Date.now() - new Date(rep.uploaded_at).getTime()) / 3600000;
      return age <= 24;
    } catch { return false; }
  };
  const ageHours = (rep) => {
    try { return Math.floor((Date.now() - new Date(rep.uploaded_at).getTime()) / 3600000); }
    catch { return null; }
  };

  return (
    <div className="space-y-5" data-testid="flow-stock-upload">
      <div>
        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Stock Upload</h1>
        <p className={`text-sm mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Upload the daily Tally stock export per firm. Every snapshot is preserved for AI reorder analysis.</p>
      </div>

      {/* Upload card */}
      <Card className={cardCls}>
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Upload size={16} className={isDark ? 'text-white/60' : 'text-gray-600'} />
              <h2 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Upload Daily Stock Excel</h2>
            </div>
            <span className={`text-[10px] inline-flex items-center gap-1 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
              <Sparkles size={11} /> AI reorder suggestions coming soon
            </span>
          </div>
          <form onSubmit={handleUpload} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Firm *</Label>
              <select className={`w-full mt-1 h-10 rounded-lg text-sm px-3 border ${selectCls}`} value={uploadFirm} onChange={e => setUploadFirm(e.target.value)} data-testid="upload-firm">
                {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <Label className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Report Date *</Label>
              <Input type="date" className={`mt-1 ${inputCls}`} value={uploadDate} onChange={e => setUploadDate(e.target.value)} data-testid="upload-date" />
            </div>
            <div className="sm:col-span-1">
              <Label className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-700'}`}>Excel File *</Label>
              <label className={`mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/70 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                <FileSpreadsheet size={14} />
                <span className="truncate flex-1">{uploadFile ? uploadFile.name : 'Select .xls / .xlsx'}</span>
                <input type="file" accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={e => setUploadFile(e.target.files?.[0] || null)} data-testid="upload-file" />
              </label>
            </div>
            <Button type="submit" disabled={uploading} className="h-10 bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid="upload-submit">
              {uploading ? 'Uploading…' : <><Upload size={14} className="mr-1.5" /> Upload</>}
            </Button>
          </form>

          <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg ${isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
            <Info size={14} className="flex-shrink-0 mt-0.5" />
            <p className="text-[11px] leading-snug">
              All historical uploads are preserved (no overwrite). The Stock Report page shows the most-recent snapshot per firm. Store users can delete their own uploads within <b>24 hours</b>; admins can delete anytime.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Upload history */}
      <Card className={cardCls}>
        <CardContent className="p-4">
          <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <History size={12} /> Upload History ({reports.length})
          </h3>
          {loading ? (
            <div className={`p-6 text-center text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Loading…</div>
          ) : reports.length === 0 ? (
            <div className={`p-6 text-center text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>No uploads yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[760px]">
                <thead className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                  <tr>
                    <th className="text-left py-2 px-2 font-medium">Firm</th>
                    <th className="text-left py-2 px-2 font-medium">Date</th>
                    <th className="text-left py-2 px-2 font-medium">File</th>
                    <th className="text-right py-2 px-2 font-medium">Items</th>
                    <th className="text-right py-2 px-2 font-medium">Total Qty</th>
                    <th className="text-left py-2 px-2 font-medium">Uploaded</th>
                    <th className="text-right py-2 px-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(r => {
                    const allow = canDelete(r);
                    const age = ageHours(r);
                    return (
                      <tr key={r.id} className={`border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-testid={`upload-row-${r.id}`}>
                        <td className="py-2 px-2"><span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>{r.firm_code}</span></td>
                        <td className={`py-2 px-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{r.report_date}</td>
                        <td className={`py-2 px-2 truncate max-w-[160px] ${isDark ? 'text-white/60' : 'text-gray-600'}`} title={r.filename}>{r.filename}</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{r.total_items.toLocaleString('en-IN')}</td>
                        <td className={`py-2 px-2 text-right tabular-nums ${isDark ? 'text-white' : 'text-gray-900'}`}>{Math.round(r.total_qty).toLocaleString('en-IN')}</td>
                        <td className={`py-2 px-2 text-[10px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                          <span className="inline-flex items-center gap-1"><CheckCircle2 size={10} className="text-green-400" /> {new Date(r.uploaded_at).toLocaleString()}</span>
                          <div>by {r.uploaded_by_name || 'Admin'} {age !== null && <span className={age > 24 ? 'text-amber-400' : ''}>· {age}h ago</span>}</div>
                        </td>
                        <td className="py-2 px-2 text-right">
                          {allow ? (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(r)} data-testid={`delete-upload-${r.id}`} title="Delete">
                              <Trash2 size={12} />
                            </Button>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`} title="Older than 24h — only admin can delete">
                              <AlertTriangle size={10} /> Locked
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FlowStockUpload;
